import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Custom hook to handle data refreshing when navigating back to list views
 * after mutations. This ensures fresh data is loaded when returning from
 * detail/edit pages.
 * 
 * @param {Function} refetch - Apollo refetch function from useQuery
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether to enable auto-refresh (default: true)
 * @param {Array} options.dependencies - Additional dependencies to trigger refresh
 */
export const useDataRefresh = (refetch, options = {}) => {
  const location = useLocation();
  const { enabled = true, dependencies = [] } = options;

  useEffect(() => {
    if (!enabled || !refetch) return;

    // Check if we're coming back from a detail/edit page with refresh state
    const shouldRefresh = location.state?.refreshData || 
                         location.state?.updatedItemId ||
                         location.state?.deletedItemId ||
                         location.state?.createdItemId;

    if (shouldRefresh) {
      // Small delay to ensure mutations have completed
      const timeoutId = setTimeout(() => {
        refetch();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [location.state, refetch, enabled, ...dependencies]);

  // Clean up navigation state after handling
  useEffect(() => {
    if (location.state?.refreshData) {
      // Clear the refresh state to prevent unnecessary refetches
      window.history.replaceState({}, '', location.pathname + location.search);
    }
  }, [location.state, location.pathname, location.search]);
};

/**
 * Hook to get navigation options for mutations
 * Returns consistent navigation state for different mutation types
 * 
 * @param {string} itemId - ID of the item being mutated
 * @param {string} mutationType - Type of mutation ('create', 'update', 'delete')
 */
export const useNavigationState = (itemId, mutationType = 'update') => {
  const getNavigationState = (targetPath) => {
    const stateMap = {
      create: { 
        refreshData: true, 
        createdItemId: itemId,
        scrollToTop: true 
      },
      update: { 
        refreshData: true, 
        updatedItemId: itemId 
      },
      delete: { 
        refreshData: true, 
        deletedItemId: itemId,
        scrollToTop: true 
      }
    };

    return {
      pathname: targetPath,
      state: stateMap[mutationType] || stateMap.update
    };
  };

  return { getNavigationState };
};
